import { useEffect, useState } from "react";
import api, { formatINR, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LogOut, Phone, Calendar, Download, Check, X, Upload, IndianRupee, AlertCircle, Pencil, Eye, User } from "lucide-react";
import { Toaster, toast } from "sonner";
import EditStudentDialog from "@/components/EditStudentDialog";
import ForceChangePasswordDialog from "@/components/ForceChangePasswordDialog";
import { ACADEMY_NAME_FULL, LOGO_URL, ACADEMY_ADDRESS, ACADEMY_PHONE } from "@/lib/brand";

export default function StudentPortal() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/students/${user.id}`);
    setMe(data);
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user?.id]);

  if (!me) return <div className="p-8 text-slate-500">Loading...</div>;

  const fs = me.fee_summary;
  const required = me.required_documents || me.program?.required_documents || [];
  const uploadedMap = Object.fromEntries((me.documents || []).map((d) => [d.doc_name, d]));
  const docsPct = required.length ? Math.round(Object.keys(uploadedMap).length / required.length * 100) : 100;
  const programs = me.programs || (me.program ? [me.program] : []);

  const uploadDoc = async (docName, file) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await api.post("/documents/upload", { student_id: me.id, doc_name: docName, file_data: reader.result, file_name: file.name });
        toast.success(`${docName} uploaded`);
        load();
      } catch {
        toast.error("Failed");
      }
    };
    reader.readAsDataURL(file);
  };

  const viewDocument = async (docId, docName) => {
    try {
      const { data } = await api.get(`/documents/${docId}`);
      setDocOpen({ ...data, doc_name: docName });
    } catch {
      toast.error("Failed to load document");
    }
  };

  const downloadReceipt = async (pid) => {
    const { data } = await api.get(`/payments/${pid}/receipt`);
    const w = window.open("", "_blank");
    w.document.write(receiptHtml(data));
    w.document.close();
    setTimeout(() => w.print(), 200);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="student-portal">
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={LOGO_URL} alt="logo" className="w-11 h-11 object-contain flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-bold truncate" style={{ fontFamily: "Chivo" }} data-testid="portal-title">{ACADEMY_NAME_FULL}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Student Portal</div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={logout} data-testid="portal-logout" className="text-white hover:bg-slate-800 flex-shrink-0">
            <LogOut size={14} className="mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Hero */}
        <Card className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0">
          <div className="flex items-start gap-4">
            {me.photo_url ? (
              <img src={me.photo_url} alt={me.name} className="w-16 h-20 rounded-md object-cover border-2 border-slate-600" />
            ) : (
              <div className="w-16 h-20 rounded-md bg-slate-700 flex items-center justify-center">
                <User size={28} className="text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-lime-300 mb-1">Welcome back</div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "Chivo" }}>{me.name}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-300">
                <span className="font-mono">{me.student_id}</span>
                <span className="flex items-center gap-1"><Phone size={12} /> {me.phone}</span>
                <span>{programs.map((p) => p.name).join(" + ")}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Fee Summary */}
        <Card className="p-6" data-testid="portal-fees">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: "Chivo" }}><IndianRupee size={18} /> Fees</h3>
            <Badge variant="outline" className={`status-${fs.status === "unset" ? "inquiry" : fs.status}`}>{fs.status === "unset" ? "Not Set" : fs.status}</Badge>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div><div className="label-caps">Total</div><div className="text-xl font-bold" style={{ fontFamily: "Chivo" }}>{formatINR(fs.total)}</div></div>
            <div><div className="label-caps">Paid</div><div className="text-xl font-bold text-emerald-700" style={{ fontFamily: "Chivo" }}>{formatINR(fs.paid)}</div></div>
            <div><div className="label-caps">Due</div><div className="text-xl font-bold text-red-700" style={{ fontFamily: "Chivo" }}>{formatINR(fs.outstanding)}</div></div>
          </div>
          <Progress value={fs.total ? (fs.paid / fs.total) * 100 : 0} />
          {fs.next_due && (
            <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-center gap-2">
              <AlertCircle size={14} /> Next installment of {formatINR(fs.next_due.amount)} is due {formatDate(fs.next_due.due_date)}
            </div>
          )}
        </Card>

        <Tabs defaultValue="profile">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="profile" data-testid="portal-tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="installments" data-testid="portal-tab-installments">Installments</TabsTrigger>
            <TabsTrigger value="receipts" data-testid="portal-tab-receipts">Receipts</TabsTrigger>
            <TabsTrigger value="documents" data-testid="portal-tab-documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold" style={{ fontFamily: "Chivo" }}>My Information</h3>
                <Button size="sm" onClick={() => setEditOpen(true)} data-testid="portal-edit-profile" className="bg-slate-900 hover:bg-slate-800">
                  <Pencil size={14} className="mr-1" /> Edit Details
                </Button>
              </div>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <KV label="Full Name" v={me.name} />
                <KV label="Phone" v={me.phone} mono />
                <KV label="Email" v={me.email} />
                <KV label="Date of Birth" v={me.date_of_birth} />
                <KV label="Age" v={me.age} />
                <KV label="Blood Group" v={me.blood_group} />
                <KV label="Gender" v={me.gender} />
                <KV label="Address" v={me.address} wide />
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="font-semibold text-sm mb-2">Parents</div>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <KV label="Father" v={me.father_name} />
                  <KV label="Mother" v={me.mother_name} />
                  <KV label="Parent Phone" v={me.parent_phone} mono />
                </div>
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="font-semibold text-sm mb-2">Physical</div>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <KV label="Height" v={me.height ? `${me.height} cm` : ""} />
                  <KV label="Chest" v={me.chest ? `${me.chest} cm` : ""} />
                  <KV label="Weight" v={me.weight ? `${me.weight} kg` : ""} />
                </div>
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="font-semibold text-sm mb-2">Programs</div>
                <div className="flex flex-wrap gap-2">
                  {programs.map((p) => (
                    <Badge key={p.id} variant="outline" className="text-sm">{p.name}{p.is_lifetime ? " · Lifetime" : ""}</Badge>
                  ))}
                </div>
              </div>
              {(me.facilities || []).length > 0 && (
                <div className="border-t pt-4 mt-4">
                  <div className="font-semibold text-sm mb-2">Facilities / आमच्या सुविधा</div>
                  <div className="flex flex-wrap gap-2">
                    {me.facilities.map((f) => (
                      <Badge key={f} variant="outline" className="text-xs">{PORTAL_FACILITY_LABELS[f] || f}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="installments" className="space-y-2 mt-4">
            {(me.installments_schedule || []).length === 0 && <Card className="p-6 text-center text-sm text-slate-500">Fee schedule not set yet</Card>}
            {(me.installments_schedule || []).map((i) => {
              const overdue = !i.paid && new Date(i.due_date) < new Date();
              return (
                <Card key={i.installment_no} className={`p-4 flex items-center justify-between ${overdue ? "border-red-300 bg-red-50" : i.paid ? "border-emerald-200 bg-emerald-50" : ""}`}>
                  <div>
                    <div className="font-semibold">Installment {i.installment_no} · {formatINR(i.amount)}</div>
                    <div className="text-xs text-slate-600 flex items-center gap-1"><Calendar size={12} /> Due {formatDate(i.due_date)}</div>
                  </div>
                  <Badge variant="outline" className={i.paid ? "status-paid" : overdue ? "status-overdue" : "status-due"}>
                    {i.paid ? "Paid" : overdue ? "Overdue" : "Due"}
                  </Badge>
                </Card>
              );
            })}
          </TabsContent>

          <TabsContent value="receipts" className="space-y-2 mt-4">
            {(me.payments || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No payments yet</div>}
            {(me.payments || []).map((p) => (
              <Card key={p.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{formatINR(p.amount)} · {p.mode.toUpperCase()}</div>
                  <div className="text-xs text-slate-500 font-mono">{p.receipt_no} · {formatDate(p.paid_on)}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => downloadReceipt(p.id)} data-testid={`portal-receipt-${p.id}`}>
                  <Download size={12} className="mr-1" /> Receipt
                </Button>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="documents" className="mt-4">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{Object.keys(uploadedMap).length} of {required.length} uploaded</span>
                <span className="text-sm font-bold">{docsPct}%</span>
              </div>
              <Progress value={docsPct} />
            </div>
            <div className="space-y-2">
              {required.map((d) => {
                const uploaded = uploadedMap[d];
                return (
                  <Card key={d} className={`p-4 flex items-center justify-between ${uploaded ? "border-emerald-200" : ""}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {uploaded ? <Check size={18} className="text-emerald-600 flex-shrink-0" /> : <X size={18} className="text-red-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d}</div>
                        {uploaded && <div className="text-xs text-slate-500 truncate">{uploaded.file_name} · {formatDate(uploaded.uploaded_at)}</div>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {uploaded && (
                        <Button size="sm" variant="outline" onClick={() => viewDocument(uploaded.id, d)} data-testid={`portal-view-${d.replace(/\s+/g, "-")}`}>
                          <Eye size={12} className="mr-1" /> View
                        </Button>
                      )}
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(d, e.target.files[0])} data-testid={`portal-upload-${d.replace(/\s+/g, "-")}`} />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 border rounded-md text-xs hover:bg-slate-100">
                          <Upload size={12} /> {uploaded ? "Replace" : "Upload"}
                        </span>
                      </label>
                    </div>
                  </Card>
                );
              })}
              {required.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No documents required</div>}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <EditStudentDialog open={editOpen} onOpenChange={setEditOpen} student={me} forStudent onSaved={load} />
      <ForceChangePasswordDialog open={!!me.must_change_password} onDone={load} />

      <Dialog open={!!docOpen} onOpenChange={(o) => !o && setDocOpen(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{docOpen?.doc_name}</DialogTitle></DialogHeader>
          {docOpen && (
            <div className="max-h-[70vh] overflow-auto">
              {docOpen.file_data?.startsWith("data:application/pdf") ? (
                <iframe src={docOpen.file_data} title={docOpen.doc_name} className="w-full h-[70vh]" />
              ) : docOpen.file_data?.startsWith("data:image") ? (
                <img src={docOpen.file_data} alt={docOpen.doc_name} className="max-w-full mx-auto" />
              ) : (
                <a href={docOpen.file_data} download={docOpen.file_name} className="text-blue-600 underline">Download {docOpen.file_name}</a>
              )}
            </div>
          )}
          <DialogFooter>
            <a href={docOpen?.file_data} download={docOpen?.file_name}>
              <Button variant="outline"><Download size={14} className="mr-1" /> Download</Button>
            </a>
            <Button onClick={() => setDocOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function KV({ label, v, mono, wide }) {
  return (
    <div className={wide ? "md:col-span-3" : ""}>
      <div className="label-caps">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}

const PORTAL_FACILITY_LABELS = {
  dress_code: "Dress Code / ड्रेस कोड",
  ground: "Ground / ग्राऊंड",
  lecture: "Lecture / लेक्चर",
  study_hall: "Study Hall / अभ्यासिका",
  hostel: "Hostel / राहण्याची सोय",
  mess: "Mess / जेवणाची सोय",
};

function receiptHtml(r) {
  const a = r.academy;
  const logoUrl = a.logo_url?.startsWith("http") ? a.logo_url : `${window.location.origin}${a.logo_url || ""}`;
  const logo = a.logo_url ? `<img src="${logoUrl}" alt="logo" style="width:80px;height:80px;object-fit:contain;margin:0 auto 10px;display:block" />` : "";
  return `<!doctype html><html><head><title>Receipt ${r.payment.receipt_no}</title>
  <style>body{font-family:'Segoe UI',Arial,sans-serif;padding:40px;max-width:640px;margin:auto;color:#0f172a}
  h1{font-size:22px;margin:0}.muted{color:#64748b}.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #e2e8f0}
  .total{font-size:20px;font-weight:bold;padding:16px;background:#dcfce7;color:#166534;border-radius:8px;margin-top:20px}
  .addr{font-size:13px;color:#475569;margin-top:6px}</style></head>
  <body><div style="text-align:center;margin-bottom:24px;border-bottom:3px solid #0f172a;padding-bottom:16px">${logo}<h1>${a.name}</h1>
  <div class="muted" style="font-size:12px">${a.name_en}</div>
  <div class="addr">${a.address}</div>
  <div class="addr">मोबाईल नंबर - ${a.phone}</div></div>
  <div><div class="row"><span class="muted">Receipt No</span><strong>${r.payment.receipt_no}</strong></div>
  <div class="row"><span class="muted">Date</span><strong>${new Date(r.payment.paid_on).toLocaleString("en-IN")}</strong></div>
  <div class="row"><span class="muted">Student</span><strong>${r.student.name} (${r.student.student_id})</strong></div>
  <div class="row"><span class="muted">Phone</span><strong>${r.student.phone}</strong></div>
  <div class="row"><span class="muted">Program</span><strong>${r.program?.name || "-"}</strong></div>
  <div class="row"><span class="muted">Payment Mode</span><strong>${r.payment.mode.toUpperCase()}</strong></div>
  ${r.payment.installment_no ? `<div class="row"><span class="muted">Installment</span><strong>#${r.payment.installment_no}</strong></div>` : ""}
  <div class="total">Amount Paid: ₹${r.payment.amount.toLocaleString("en-IN")}</div>
  ${r.payment.notes ? `<div style="margin-top:20px"><span class="muted">Notes:</span> ${r.payment.notes}</div>` : ""}
  <div style="margin-top:40px;text-align:center" class="muted">Thank you. Computer-generated receipt.</div></div></body></html>`;
}
